/**
 * OKR Type Definitions (shared between web and mobile)
 */

import { UserStatus } from './api';

export interface KeyResult {
  id: string;
  tenantId: string;
  okrId: string;
  title: string;
  description?: string;
  unitType: 'percentage' | 'number' | 'currency' | 'boolean';
  startValue: number;
  targetValue: number;
  currentValue: number;
  unit?: string;
  ownerId?: string;
  // Backlog B1, 2026-08-12. This was typed `SafeUser` — the whole user record minus the
  // password hash — while the API sent NOTHING: `getOKRById`'s key-results include never
  // expanded the relation, so the field was permanently undefined and the type promised
  // a dozen properties that could not arrive. The include now exists and returns exactly
  // these three, so the type says three. Widening it means widening that Prisma select
  // first (okr.service.ts, `keyResults.include.owner`) — every projection is an allow-list.
  owner?: { id: string; firstName: string; lastName: string };
  createdById?: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  status: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  progressUpdates?: ProgressUpdate[];
}

export interface ProgressUpdate {
  id: string;
  tenantId: string;
  keyResultId: string;
  updatedById: string;
  updatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  previousValue: number;
  newValue: number;
  notes?: string;
  createdAt: string;
}

/**
 * The legal OKR types. Mirrors `OKR_TYPES` in `backend/src/constants/okr.constants.ts`,
 * which is what the API validator and the CSV import path both derive from.
 *
 * 'personal' was RETIRED on 2026-08-08. It was never accepted by the API validator, but
 * `shared` declared it, the CSV import path created it, and the CSV template advertised
 * it — so rows imported that way could not afterwards be edited (HTTP 422).
 *
 * Legacy 'personal' rows were deliberately left in the database rather than re-typed,
 * because re-typing them to 'individual' would widen who can read them. They are
 * owner-only on the backend, forever. Every display map that keys off `OKR.type` is a
 * `Record<string, …>` with a fallback, so such a row still renders; it is simply no
 * longer offered, filtered for, or creatable.
 */
export type OKRType = 'company' | 'department' | 'team' | 'individual';

export interface OKR {
  id: string;
  tenantId: string;
  title: string;
  description?: string;
  type: OKRType;
  ownerId: string;
  owner: {
    id: string;
    firstName: string;
    lastName: string;
    /**
     * Only present when the caller is entitled to this owner's email address
     * (own record, account_admin/super_admin, or the caller's direct report).
     * Names are always present; addresses are not. See utils/emailVisibility.ts.
     */
    email?: string;
    profilePicture?: string;
    department?: string;
    position?: string;
    managerId?: string;
    status?: UserStatus;
    deletedAt?: string | null;
  };
  departmentId?: string;
  teamId?: string;
  department?: {
    id: string;
    name: string;
  };
  createdById: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  parentOKRId?: string;
  parentOKR?: {
    id: string;
    title: string;
    type: string;
  };
  cycleId?: string;
  cycle?: {
    id: string;
    name: string;
    type: string;
    status: string;
    startDate: string;
    endDate: string;
  };
  status: 'draft' | 'active' | 'at_risk' | 'on_track' | 'off_track' | 'completed' | 'cancelled' | 'deleted';
  visibility: string;
  requiresApproval: boolean;
  approvalStatus?: string;
  startDate?: string;
  endDate?: string;
  checkInFrequency?: 'weekly' | 'biweekly' | 'monthly' | 'none' | null;
  checkInDay?: number | null;
  nextCheckInDue?: string | null;
  lastCheckInDate?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  keyResults: KeyResult[];
  childOKRs?: Array<{
    id: string;
    title: string;
    type: string;
    status: string;
    owner: {
      firstName: string;
      lastName: string;
    };
    // Backlog B2. The child's OWN progress, computed server-side from a `keyResults`
    // sub-select that is dropped before the response is sent — so there is deliberately
    // no `keyResults` here, and adding one would mean widening that select first. A
    // child with no key results of its own reports 0, per S1-o; it does not inherit.
    progress: number;
  }>;
  progress?: number;
  _count?: {
    keyResults: number;
    comments: number;
  };
}

export interface CreateOKRRequest {
  title: string;
  description?: string;
  type: OKRType;
  ownerId: string;
  departmentId?: string;
  teamId?: string;
  parentOKRId?: string;
  cycleId?: string;
  startDate?: string;
  endDate?: string;
  keyResults?: Array<{
    title: string;
    description?: string;
    unitType: 'percentage' | 'number' | 'currency' | 'boolean';
    startValue: number;
    targetValue: number;
    unit?: string;
    ownerId?: string;
  }>;
}

export interface UpdateOKRRequest {
  title?: string;
  description?: string;
  type?: OKRType;
  ownerId?: string;
  departmentId?: string | null;
  teamId?: string | null;
  status?: string;
  parentOKRId?: string | null;
  cycleId?: string | null;
  startDate?: string;
  endDate?: string;
}

export interface AddKeyResultRequest {
  title: string;
  description?: string;
  unitType: 'percentage' | 'number' | 'currency' | 'boolean';
  startValue: number;
  targetValue: number;
  unit?: string;
  ownerId?: string | null;
}

export interface UpdateProgressRequest {
  value: number;
  notes?: string;
}

export interface OKRFilters {
  search?: string;
  types?: string[];
  statuses?: string[];
  ownerIds?: string[];
  departmentIds?: string[];
  cycleIds?: string[];
  progressMin?: number;
  progressMax?: number;
  startDate?: string;
  endDate?: string;
  filterMode?: 'AND' | 'OR';
  sortBy?: 'progress' | 'dueDate' | 'title' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface OKRListResponse {
  okrs: OKR[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface FilterMetadata {
  types: string[];
  statuses: string[];
  owners: Array<{
    id: string;
    name: string;
    /**
     * Only present when the caller is entitled to this owner's email address
     * (own record, account_admin/super_admin, or the caller's direct report).
     */
    email?: string;
    department?: string;
  }>;
  /**
   * Live `Department` rows (id/name), scoped to owners of the tenant's non-deleted OKRs —
   * was free-text names (`string[]`). The OKR list department filter takes `Department.id`
   * uuids only (`GET /api/okrs?departmentIds=<name>` is a 422), the same as Reports.
   */
  departments: Array<{ id: string; name: string }>;
  cycles: Array<{
    id: string;
    name: string;
    type: string;
    status: string;
    startDate: string;
    endDate: string;
    isActive: boolean;
  }>;
  progressRange: {
    min: number;
    max: number;
  };
  dateRange: {
    min: string | null;
    max: string | null;
  };
}

export interface ActiveFilter {
  field: string;
  label: string;
  value: string | number;
  displayValue: string;
}

export type SortOption = {
  value: 'progress' | 'dueDate' | 'title' | 'updatedAt';
  label: string;
};

export const SORT_OPTIONS: SortOption[] = [
  { value: 'updatedAt', label: 'Recently Updated' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'progress', label: 'Progress' },
  { value: 'title', label: 'Title' }
];

export interface FilterPreset {
  id: string;
  tenantId: string;
  userId: string;
  name: string;
  description?: string;
  filters: OKRFilters;
  isDefault: boolean;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFilterPresetRequest {
  name: string;
  description?: string;
  filters: OKRFilters;
  isDefault?: boolean;
  isPublic?: boolean;
}

export interface UpdateFilterPresetRequest {
  name?: string;
  description?: string;
  filters?: OKRFilters;
  isDefault?: boolean;
  isPublic?: boolean;
}

// ============================================
// CHECK-IN TYPES
// ============================================

export type CheckInStatus = 'on_track' | 'at_risk' | 'off_track';

/** Per-KR narrative captured in a single check-in (read shape). */
export interface CheckInKeyResultNote {
  id: string;
  keyResultId: string;
  accomplishments?: string | null;
  challenges?: string | null;
  nextSteps?: string | null;
  keyResult?: { id: string; title: string };
}

/** Per-KR narrative submitted with a check-in (write shape). */
export interface CheckInKeyResultNoteInput {
  keyResultId: string;
  accomplishments?: string;
  challenges?: string;
  nextSteps?: string;
}

/**
 * AI "Summarize with AI" precondition: allowed only when at least one KR has
 * BOTH Accomplishments and Challenges filled (Next Steps optional).
 * Shared by the frontend button-enabled state and the backend summarize guard.
 */
export const canSummarizeCheckInNotes = (
  notes: CheckInKeyResultNoteInput[] | undefined
): boolean =>
  (notes ?? []).some(
    (n) => !!n.accomplishments?.trim() && !!n.challenges?.trim()
  );

export interface CheckIn {
  id: string;
  tenantId: string;
  okrId: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string;
  };
  status: CheckInStatus;
  accomplishments?: string;
  challenges?: string;
  nextSteps?: string;
  confidenceLevel?: number;
  dueDate?: string;
  progressPercentage?: number;
  isOverdue?: boolean;
  keyResultUpdates?: Array<{
    keyResultId: string;
    previousValue: number;
    newValue: number;
  }>;
  overallSummary?: string | null;
  keyResultNotes?: CheckInKeyResultNote[];
  checkInDate: string;
  createdAt: string;
  updatedAt: string;
  aiAssessment?: {
    quality: 'good' | 'fair' | 'poor';
    summary: string;
    flags: string[];
    generatedAt: string;
    tokensUsed?: number;
  } | null;
}

export interface CreateCheckInRequest {
  status: CheckInStatus;
  accomplishments?: string;
  challenges?: string;
  nextSteps?: string;
  confidenceLevel?: number;
  keyResultUpdates?: Array<{
    keyResultId: string;
    newValue: number;
  }>;
  overallSummary?: string;
  keyResultNotes?: CheckInKeyResultNoteInput[];
}

export interface CheckInSchedule {
  checkInFrequency: 'weekly' | 'biweekly' | 'monthly' | 'none' | null;
  checkInDay: number | null;
  nextCheckInDue: string | null;
  lastCheckInDate: string | null;
}

export interface UpdateCheckInScheduleRequest {
  checkInFrequency?: 'weekly' | 'biweekly' | 'monthly' | 'none';
  checkInDay?: number;
}

export interface CheckInListResponse {
  checkIns: CheckIn[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PendingCheckInItem {
  okrId: string;
  okrTitle: string;
  okrStatus: string;
  okrType: string;
  nextCheckInDue: string;
  lastCheckInDate: string | null;
  lastCheckInStatus: CheckInStatus | null;
  lastConfidenceLevel: number | null;
  progress: number;
  keyResultsCount: number;
  owner?: {
    id: string;
    firstName: string;
    lastName: string;
    avatar?: string | null;
  };
}

export interface PendingCheckInsResponse {
  overdue: PendingCheckInItem[];
  dueToday: PendingCheckInItem[];
  dueThisWeek: PendingCheckInItem[];
}

export interface CheckInWithOKR extends CheckIn {
  okr: {
    id: string;
    title: string;
  };
}

export interface CheckInHistoryResponse {
  checkIns: CheckInWithOKR[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CheckInHistoryFilters {
  okrId?: string;
  startDate?: string;
  endDate?: string;
  status?: CheckInStatus;
  page?: number;
  limit?: number;
}

export interface HealthDistribution {
  onTrack: number;
  atRisk: number;
  offTrack: number;
  noCheckIn: number;
  total: number;
}

export interface ComplianceMetrics {
  onTime: number;
  overdue: number;
  total: number;
}

export interface ConfidenceTrendPoint {
  date: string;
  averageConfidence: number;
  checkInCount: number;
}

export interface TeamMemberOverview {
  userId: string;
  userName: string;
  avatar?: string | null;
  totalOKRs: number;
  pendingCheckIns: number;
  lastCheckIn: string | null;
  averageConfidence: number | null;
  lastStatus: CheckInStatus | null;
}

export interface CheckInInsights {
  healthDistribution: HealthDistribution;
  compliance: ComplianceMetrics;
  confidenceTrend: ConfidenceTrendPoint[];
  teamOverview: TeamMemberOverview[];
}

export interface CalendarEvent {
  id: string;
  date: string;
  type: 'due' | 'completed';
  okrId: string;
  okrTitle: string;
  status?: CheckInStatus;
  confidenceLevel?: number | null;
  isOverdue?: boolean;
}

export interface CalendarDataResponse {
  events: CalendarEvent[];
  startDate: string;
  endDate: string;
}
